import BaseAPI from "./BaseAPI";
import { rotaEntryRoute } from "../routes";
import { newIdempotencyKey } from "@/composables/globalMethods";

export default class RotaEntryAPI extends BaseAPI {
    constructor(emitterInstance) {
        super(emitterInstance, rotaEntryRoute, "rota-entry");
    }

    async Add(data, idempotencyKey = newIdempotencyKey()) {
        return super.Add(data, idempotencyKey);
    }

    /**
     * List rota entries with optional filters
     * @param {Object} filters - Filter options
     * @param {string} [filters.start] - Start date (YYYY-MM-DD)
     * @param {string} [filters.end] - End date (YYYY-MM-DD)
     * @param {string} [filters.userId] - Filter by user ID
     * @param {string} [filters.locationId] - Filter by location ID
     * @param {boolean} [filters.published] - Filter by published status
     * @returns {Promise<Array>} Array of rota entries
     */
    async ListFiltered(filters = {}) {
        const params = new URLSearchParams();
        if (filters.start) params.append("start", filters.start);
        if (filters.end) params.append("end", filters.end);
        if (filters.userId) params.append("user_id", filters.userId);
        if (filters.locationId) params.append("location_id", filters.locationId);
        if (filters.published !== undefined) params.append("published", filters.published.toString());

        const query = params.toString() ? `?${params.toString()}` : "";
        return this._request("get", this._url(query));
    }

    /**
     * Get the current user's upcoming schedule
     * @returns {Promise<Array>} Array of upcoming rota entries for current user
     */
    async GetMySchedule() {
        return this._request("get", this._url("/my-schedule"));
    }

    /**
     * Create multiple rota entries in bulk
     * @param {Array} entries - Array of rota entry objects
     * @returns {Promise<Array>} Array of created rota entries
     */
    async AddBulk(entries, idempotencyKey = newIdempotencyKey()) {
        const result = await this._request("post", this._url("/bulk"), { entries }, {
            headers: { "Idempotency-Key": idempotencyKey }
        });
        this._emit("add-bulk");
        return result;
    }

    /**
     * Copy a week's schedule to another week
     * @param {string} sourceStartDate - Start date of source week (YYYY-MM-DD)
     * @param {string} targetStartDate - Start date of target week (YYYY-MM-DD)
     * @param {string} [locationId] - Optional location ID filter
     * @returns {Promise<Object>} Result with count of copied entries
     */
    async CopyWeek(sourceStartDate, targetStartDate, options = {}) {
        const data = {
            source_start_date: sourceStartDate,
            target_start_date: targetStartDate,
            repeat_count: options.repeatCount ?? 1,
            interval_weeks: options.intervalWeeks ?? 1,
            mode: options.mode ?? "merge"
        };
        if (options.locationId) data.location_id = options.locationId;

        const result = await this._request("post", this._url("/copy-week"), data, {
            headers: { "Idempotency-Key": options.idempotencyKey ?? newIdempotencyKey() }
        });
        this._emit("copy-week");
        return result;
    }

    /**
     * Publish a week's schedule (makes it visible to employees)
     * @param {string} startDate - Start date (YYYY-MM-DD)
     * @param {string} endDate - End date (YYYY-MM-DD)
     * @param {string} [locationId] - Optional location ID filter
     * @returns {Promise<Object>} Result with count of published entries
     */
    async PublishWeek(startDate, endDate, options = {}) {
        const data = {
            start: startDate,
            end: endDate,
            acknowledge_warnings: Boolean(options.acknowledgeWarnings)
        };
        if (options.locationId) data.location_id = options.locationId;
        const result = await this._request("post", this._url("/publish"), data, {
            headers: { "Idempotency-Key": options.idempotencyKey ?? newIdempotencyKey() }
        });
        this._emit("publish-week");
        return result;
    }

    async ValidateWeek(startDate, endDate, locationId = null) {
        const params = new URLSearchParams({ start: startDate, end: endDate });
        if (locationId) params.append("location_id", locationId);
        return this._request("get", this._url(`/validate?${params.toString()}`));
    }

    async PublishHistory(startDate, endDate, locationId = null) {
        const params = new URLSearchParams({ start: startDate, end: endDate });
        if (locationId) params.append("location_id", locationId);
        return this._request("get", this._url(`/publish-history?${params.toString()}`));
    }

    async ListAvailability(filters = {}) {
        const params = new URLSearchParams();
        if (filters.start) params.append("start", filters.start);
        if (filters.end) params.append("end", filters.end);
        if (filters.userId) params.append("user_id", filters.userId);
        const query = params.toString() ? `?${params.toString()}` : "";
        return (await this._request("get", `${this._url().replace("/rota-entries", "/rota-availability")}${query}`)) ?? [];
    }

    async SetAvailability(data, idempotencyKey = newIdempotencyKey()) {
        return this._request("post", this._url().replace("/rota-entries", "/rota-availability"), data, {
            headers: { "Idempotency-Key": idempotencyKey }
        });
    }

    async DeleteAvailability(id) {
        return this._request("delete", `${this._url().replace("/rota-entries", "/rota-availability")}/${id}`);
    }
}
